<template>
    <form-wrapper @form-submit="submit">
        <form-row>
            <form-cell>
                <text-input
                    :formData="data"
                    :label="$t('forms.offer.create_offer.fields.name.label')"
                    :placeholder="$t('forms.offer.create_offer.fields.name.placeholder')"
                    :isRequired="true"
                    type="text"
                    valuePath="title"
                    @change="change"
                ></text-input>
            </form-cell>
        </form-row>

        <form-row>
            <form-cell width="half">
                <select-input
                    :formData="data"
                    :label="$t('forms.offer.create_offer.fields.category.label')"
                    :placeholder="$t('forms.offer.create_offer.fields.category.placeholder')"
                    :options="serviceCategories"
                    :isRequired="true"
                    valuePath="serviceGroupId"
                    :isSearchable="true"
                    @change="categoryChange"
                    @clear="categoryClear"
                ></select-input>
            </form-cell>

            <form-cell width="half">
                <select-input
                    ref="serviceSubCategories"
                    :formData="data"
                    :label="$t('forms.offer.create_offer.fields.subcategory.label')"
                    :placeholder="$t('forms.offer.create_offer.fields.subcategory.placeholder')"
                    :isDisabled="!data.serviceGroupId"
                    :options="serviceSubCategories"
                    :isRequired="true"
                    valuePath="serviceId"
                    :isSearchable="true"
                    @change="change"
                    @clear="clear"
                ></select-input>
            </form-cell>
        </form-row>

        <form-row align="center">
            <form-cell v-if="adminCompanies.length" width="half">
                <select-input
                    :formData="data"
                    :label="$t('forms.offer.create_offer.fields.company.label')"
                    :placeholder="$t('forms.offer.create_offer.fields.company.placeholder')"
                    :options="adminCompanies"
                    :isNullable="true"
                    valuePath="companyId"
                    :isSearchable="true"
                    @change="companyChange"
                    @clear="companyClear"
                ></select-input>
            </form-cell>

            <form-cell :width="adminCompanies.length ? 'half' : ''">
                <select-input
                    v-if="locationOptions?.length"
                    ref="locationSelect"
                    :formData="data"
                    :label="$t('forms.offer.create_offer.fields.location.label')"
                    :placeholder="$t('forms.offer.create_offer.fields.location.placeholder')"
                    :options="locationOptions"
                    :isRequired="true"
                    :error="companyLocationsError"
                    valuePath="locationId"
                    :isSearchable="true"
                    @change="change"
                    @clear="clear"
                ></select-input>
                <p v-else>
                    <common-buttons>
                        <common-button
                            type="primary"
                            size="small"
                            :label="$t('forms.offer.create_offer.buttons.add_location')"
                            @click="addLocation()"
                        ></common-button>
                    </common-buttons>
                </p>
            </form-cell>
        </form-row>

        <form-row>
            <form-cell width="half" class="working-schedule-slot">
                <select-input
                    :formData="data"
                    :label="$t('forms.offer.create_offer.fields.reservation_type.label')"
                    :placeholder="$t('forms.offer.create_offer.fields.reservation_type.placeholder')"
                    :options="OfferReservationTypeEnum"
                    :isRequired="true"
                    valuePath="type"
                    @change="changeTypeReseration"
                ></select-input>
                <common-info-tip class="working-schedule-slot__tooltip">
                    <span>
                        {{ $t('forms.offer.create_offer.fields.reservation_type.tooltip') }}
                    </span>
                </common-info-tip>
            </form-cell>
            <form-cell width="half" class="working-schedule-slot">
                <select-input
                    :formData="data"
                    :label="$t('forms.offer.create_offer.fields.reservation_in_advance.label')"
                    :placeholder="$t('forms.offer.create_offer.fields.reservation_in_advance.placeholder')"
                    :options="appointmentScheduleInAdvanceOptions"
                    valuePath="reservationTimeLimitInMinutes"
                    @change="change"
                ></select-input>
                <common-info-tip class="working-schedule-slot__tooltip">
                    <span>
                        {{ $t('forms.offer.create_offer.fields.reservation_in_advance.tooltip') }}
                    </span>
                </common-info-tip>
            </form-cell>
        </form-row>
        <form-row>
            <form-cell width="half" class="working-schedule-slot">
                <select-input
                    :formData="data"
                    :label="$t('forms.offer.create_offer.fields.cancellation_in_advance.label')"
                    :placeholder="$t('forms.offer.create_offer.fields.cancellation_in_advance.placeholder')"
                    :options="cancelLimitForInitiators"
                    valuePath="cancelLimitForInitiators"
                    @change="change"
                ></select-input>
                <common-info-tip class="working-schedule-slot__tooltip">
                    <span>
                        {{ $t('forms.offer.create_offer.fields.cancellation_in_advance.tooltip') }}
                    </span>
                </common-info-tip>
            </form-cell>
            <form-cell width="half" class="working-schedule-slot">
                <select-input
                    :formData="data"
                    :label="$t('forms.offer.create_offer.fields.rescheduling_in_advance.label')"
                    :placeholder="$t('forms.offer.create_offer.fields.rescheduling_in_advance.placeholder')"
                    :options="rescheduleLimitForInitiators"
                    valuePath="rescheduleLimitForInitiators"
                    @change="change"
                ></select-input>
                <common-info-tip class="working-schedule-slot__tooltip">
                    <span>
                        {{ $t('forms.offer.create_offer.fields.rescheduling_in_advance.tooltip') }}
                    </span>
                </common-info-tip>
            </form-cell>
        </form-row>
        <form-row>
            <form-cell width="half" class="working-schedule-slot">
                <text-input
                    type="number"
                    :formData="data"
                    :label="$t('forms.offer.create_offer.fields.pause.label')"
                    :placeholder="$t('forms.offer.create_offer.fields.pause.placeholder')"
                    :isRequired="true"
                    valuePath="workingScheduleSlot"
                    @change="change"
                ></text-input>
                <common-info-tip class="working-schedule-slot__tooltip">
                    <span>
                        {{ $t('forms.offer.create_offer.fields.pause.tooltip') }}
                    </span>
                </common-info-tip>
            </form-cell>
            <form-cell width="half">
                <select-input
                    :formData="data"
                    :label="$t('forms.offer.create_offer.fields.currency.label')"
                    :placeholder="$t('forms.offer.create_offer.fields.currency.placeholder')"
                    :options="currencyOptions"
                    :isRequired="true"
                    valuePath="currency"
                    @change="change"
                ></select-input>
            </form-cell>
        </form-row>

        <form-row>
            <form-cell>
                <textarea-input
                    :formData="data"
                    :label="$t('forms.offer.create_offer.fields.description.label')"
                    :placeholder="$t('forms.offer.create_offer.fields.description.placeholder')"
                    :isRequired="true"
                    type="text"
                    :maxlength="600"
                    valuePath="description"
                    @change="change"
                ></textarea-input>
            </form-cell>
        </form-row>

        <template #footer>
            <common-buttons>
                <common-button :isProcessing="isLoading" type="primary" :label="$t('common.microcopy.next')"></common-button>
            </common-buttons>
        </template>
    </form-wrapper>
</template>

<script>
    import FormMixin from 'mixins/FormMixin'
    import CurrencyEnum from 'enums/currency.enum'
    import OfferTypeEnum from 'enums/offer_type.enum'
    import AppointmentLengthEnum from 'enums/appointment_length.enum'
    import AppointmentScheduleInAdvanceEnum from 'enums/appointment_schedule_in_advance.enum'
    import InitiatorRescheduleOrCancelLimitEnum from 'enums/reschedule_or_cancel_limit_for_initiator.enum'
    import ServiceCategoryAPI from 'api/ServiceCategoryAPI'
    import CompanyAPI from 'api/CompanyAPI'
    import OfferAPI from 'api/OfferAPI'
    import RoutesEnum from 'enums/routes.enum'
    import LanguageEnum from 'enums/language.enum'
    import PriceMixin from 'mixins/PriceMixin'
    import CompanyRoles from 'enums/company_roles.enum'
    import CurrencyOptionsEnum from 'enums/currency_options.enum'
    import LocationTypeEnum from 'enums/location_type.enum'
    import OfferReservationTypeEnum from 'enums/offer_reservation_type.enum'

    export default {
        mixins: [FormMixin, PriceMixin],

        props: {
            prefill: { type: Object, default: undefined },
        },

        data() {
            return {
                currencyOptions: Object.values(CurrencyEnum).map((currency) => ({ label: currency, value: currency })),
                allCategories: [],
                selectedSubCategories: [],
                userLocations: this.$auth
                    .getUser()
                    ?.locationsForUser?.filter((location) => location.locationType === LocationTypeEnum.BUSINESS)
                    .map((location) => ({
                        label: `${location.address}, ${location.city.name}`,
                        value: location.id,
                    })),
                companyLocations: [],
                adminCompanies: this.$auth
                    .getUser()
                    ?.companyUsers?.filter((cr) => cr.companyRole === CompanyRoles.COMPANY_ADMIN.VALUE && !cr.company.deletedAt)
                    .map((cr) => ({ label: cr.company.name, value: cr.company.id })),
                appointmentScheduleInAdvanceOptions: [],
                serviceSubCategories: [],
                LocationTypeEnum: LocationTypeEnum,
                OfferReservationTypeEnum: Object.values(OfferReservationTypeEnum).map((OfferReservationTypeEnum) => ({
                    label: this.$t(`forms.offer.create_offer.reservation_type.${OfferReservationTypeEnum.toLowerCase()}`),

                    value: OfferReservationTypeEnum,
                })),
                initialData: null,
            }
        },

        computed: {
            appointmentLengthOptions() {
                return AppointmentLengthEnum[this.$i18n.locale]
            },

            rescheduleLimitForInitiators() {
                return InitiatorRescheduleOrCancelLimitEnum[this.$i18n.locale]
            },

            cancelLimitForInitiators() {
                return InitiatorRescheduleOrCancelLimitEnum[this.$i18n.locale]
            },

            locationOptions() {
                return this.data.companyId ? this.companyLocations : this.userLocations
            },

            companyLocationsError() {
                return this.data.companyId && !this.companyLocations.length
                    ? this.$t('forms.offer.create_offer.errors.location.company_has_no_locations')
                    : undefined
            },

            isServiceForCompany() {
                return !!this.data.companyId
            },

            offerType() {
                return this.isServiceForCompany ? OfferTypeEnum.COMPANY : OfferTypeEnum.USER
            },

            serviceCategories() {
                return this.allCategories.map((category) => ({
                    label:
                        this.$i18n.locale === LanguageEnum.EN
                            ? category.name
                            : category[`name${this.$i18n.locale.toUpperCase()}`],
                    value: category.id,
                }))
            },
        },

        methods: {
            submit() {
                const req = this.isServiceForCompany
                    ? this.data.id
                        ? OfferAPI.modifyCompanyOffer
                        : OfferAPI.createCompanyOffer
                    : this.data.id
                    ? OfferAPI.modifyUserOffer
                    : OfferAPI.createUserOffer
                this.data.workingScheduleSlot = Number(this.data.workingScheduleSlot)

                if (this.checkIfFormChanged(this.data)) {
                    this.submitForm(req(this.data), { scrollTop: true })
                        .then((res) => {
                            this.$router.push({ name: RoutesEnum.OFFER.CONFIGURE, params: { id: res.id, type: this.offerType } })
                            this.emitEvent({ name: 'offer-changed', payload: res })
                        })
                        .catch((error) => {
                            const locationError = error?.error?.invalidFields.find(
                                (item) => item.message === 'IS_NUMBER' && item.field === 'locationId',
                            )

                            if (locationError) {
                                this.$toast.error(this.$t('common.errors.location_required'))
                            }
                        })
                } else {
                    this.$router.push({ name: RoutesEnum.OFFER.CONFIGURE, params: { id: this.data.id, type: this.offerType } })
                    this.emitEvent({ name: 'offer-changed' })
                }
            },

            checkIfFormChanged(data) {
                delete data.errors
                return this.sortData(data) !== this.sortData(this.initialData)
            },

            sortData(obj) {
                return JSON.stringify(
                    Object.keys(obj)
                        .sort()
                        .reduce((acc, key) => {
                            acc[key] = obj[key]
                            return acc
                        }, {}),
                )
            },

            addLocation() {
                if (this.isServiceForCompany) {
                    this.$router.push({
                        name: RoutesEnum.COMPANY.MY_COMPANY,
                        params: { id: this.data.companyId, tabType: 'locations' },
                        query: { tab: 'locations' },
                    })
                } else {
                    this.$router.push({ name: RoutesEnum.USER.ME, params: { tabType: 'locations' }, query: { tab: 'locations' } })
                }
            },

            categoryChange(event) {
                this.change(event)
                this.data.serviceId = undefined
                this.$refs.serviceSubCategories.searchTerm = ''
                this.loadServiceSubCategories(this.data.serviceGroupId)
                this.getServiceSubCategories()
            },

            loadServiceSubCategories(serviceGroupId) {
                this.selectedSubCategories = this.allCategories.find((category) => category.id === serviceGroupId)?.services
            },

            companyChange(event) {
                if (!event.value) {
                    return
                }
                this.change(event)
                this.data.locationId = undefined
                if (this.$refs.locationSelect) {
                    this.$refs.locationSelect.searchTerm = ''
                }
                this.loadCompanyLocations(this.data.companyId)
            },

            changeTypeReseration(event) {
                this.change(event)
                this.appointmentScheduleInAdvanceOptions = [
                    OfferReservationTypeEnum.ACCEPTED_BY_DEFAULT,
                    OfferReservationTypeEnum.BLOCK_PUBLIC_RESERVATIONS,
                ].includes(event.value)
                    ? AppointmentScheduleInAdvanceEnum[this.$i18n.locale]
                    : AppointmentScheduleInAdvanceEnum[this.$i18n.locale].slice(0, -4)

                const appointmentScheduleValues = this.appointmentScheduleInAdvanceOptions.map((el) => el.value)

                if (!appointmentScheduleValues.includes(this.data.reservationTimeLimitInMinutes)) {
                    this.data.reservationTimeLimitInMinutes = appointmentScheduleValues.pop()
                }
            },

            loadCompanyLocations(companyId) {
                return CompanyAPI.getById(companyId).then((company) => {
                    this.data.currency = this.currencyOptions[company.city.country.id ?? CurrencyOptionsEnum.RSD].value

                    this.companyLocations = company.locationsForCompany
                        .filter((location) => location.locationType === LocationTypeEnum.BUSINESS)
                        .map((location) => ({
                            label: `${location.address}, ${location.city.name}`,
                            value: location.id,
                        }))
                })
            },

            companyClear(event) {
                this.clear(event)
                this.data.locationId = undefined
                this.$refs.locationSelect.searchTerm = ''
            },

            categoryClear(event) {
                this.clear(event)
                this.data.serviceId = undefined
                this.$refs.serviceSubCategories.searchTerm = ''
            },

            fetchServiceCategories() {
                return ServiceCategoryAPI.getAll().then((categories) => {
                    this.allCategories = categories
                })
            },

            findServiceCategoryByServiceId(serviceId) {
                return this.allCategories
                    .map((category) => ({
                        id: category.id,
                        name: category.name,
                        services: category.services.map((service) => service.id),
                    }))
                    .find((category) => category.services.includes(serviceId))
            },

            async prefillOffer() {
                if (this.prefill) {
                    const price = this.setPrice(this.prefill[`price${this.prefill.currency}`])

                    const {
                        id,
                        appointmentLength,
                        currency,
                        location,
                        service,
                        reservationTimeLimitInMinutes,
                        cancelLimitForInitiators,
                        rescheduleLimitForInitiators,
                        workingScheduleSlot,
                        type,
                    } = this.prefill
                    const data = {
                        id,
                        appointmentLength,
                        currency,
                        price: price,
                        reservationTimeLimitInMinutes: reservationTimeLimitInMinutes ? reservationTimeLimitInMinutes : 20,
                        cancelLimitForInitiators: cancelLimitForInitiators ?? 60,
                        rescheduleLimitForInitiators: rescheduleLimitForInitiators ?? 60,
                        workingScheduleSlot: workingScheduleSlot ?? 30,
                        type: type ?? OfferReservationTypeEnum.REQUEST_APPROVAL,
                    }
                    data.description = this.prefill?.descriptionTextContent?.originalText
                    data.title = this.prefill?.titleTextContent?.originalText

                    if (service) {
                        data.serviceId = service.id
                        const { id: serviceGroupId } = this.findServiceCategoryByServiceId(data.serviceId)
                        this.loadServiceSubCategories(serviceGroupId)
                        data.serviceGroupId = serviceGroupId
                    }

                    const companyId = location?.companyServiceProvider?.id
                    if (companyId) {
                        data.companyId = companyId
                        await this.loadCompanyLocations(companyId).then(() => (data.locationId = location.id))
                    } else {
                        data.locationId = location?.id ? location?.id : 0
                    }
                    this.getAppointmentScheduleInAdvanceOptions(data.type)
                    this.prefillData(data)
                    this.initialData = data
                    setTimeout(() => {
                        this.$refs.locationSelect.select(data.locationId)
                    }, 0)
                }
            },

            getAppointmentScheduleInAdvanceOptions(type) {
                this.appointmentScheduleInAdvanceOptions = [
                    OfferReservationTypeEnum.ACCEPTED_BY_DEFAULT,
                    OfferReservationTypeEnum.BLOCK_PUBLIC_RESERVATIONS,
                ].includes(type)
                    ? AppointmentScheduleInAdvanceEnum[this.$i18n.locale]
                    : AppointmentScheduleInAdvanceEnum[this.$i18n.locale].slice(0, -4)
            },

            getServiceSubCategories() {
                this.serviceSubCategories = this.selectedSubCategories?.map((subcategory) => ({
                    label:
                        this.$i18n.locale === LanguageEnum.EN
                            ? subcategory.name
                            : subcategory[`name${this.$i18n.locale.toUpperCase()}`],
                    value: subcategory.id,
                }))
            },
        },

        mounted() {
            this.fetchServiceCategories()
                .then(() => {
                    const { service } = this.prefill
                    if (service) {
                        const { id: serviceGroupId } = this.findServiceCategoryByServiceId(service.id)
                        this.loadServiceSubCategories(serviceGroupId)
                    }
                    this.getServiceSubCategories()
                })
                .then(() => {
                    this.prefillOffer()
                })
        },
    }
</script>
<style lang="scss" scoped>
    .working-schedule-slot {
        position: relative;
        &__tooltip {
            position: absolute;
            top: 22px;
            right: 15px;
        }
    }
</style>
